"use client";

import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Avatar,
  Grid,
  InputAdornment,
  Tooltip,
  Chip,
  Rating,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Call,
  Close as CloseIcon,
  Email,
  Search as SearchIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import CustomTextField from "../../../../components/common/Textfield";
import type { Role } from "./CallSheetPage";
import type { IUser } from "../../../../types/callsheet";
import Paginator from "../../../../components/common/Paginator";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import type { GridPaginationModel } from "@mui/x-data-grid";
import AddCrewModal from "./AddCrewModal";
import { useNavigate, useParams } from "react-router-dom";
import type { IColorTokens, IThemeState } from "../../../../types/theme";

interface AssignCrewModalProps {
  open: boolean;
  onClose: () => void;
  onAssign: (user: IUser, roleIds: number[]) => Promise<boolean>;
  availableRoles: Role[];
  theme: IThemeState;
  colors: IColorTokens;
  maxAssignments?: number;
  currentAssignments?: number;
  onSuccessClose: () => void;
}

interface CallSheetAssignment {
  id: number;
  crew_member: {
    id: number;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
  };
  stream: {
    id: number;
    title: string;
  };
  roles: number[]; // roles is an array of role IDs at the top level
  assignment: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

const AssignCrewModal: React.FC<AssignCrewModalProps> = ({
  open,
  onClose,
  onAssign,
  availableRoles,
  theme,
  colors,
  onSuccessClose,
  maxAssignments = 8,
  currentAssignments = 0,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [crewMembers, setCrewMembers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const assigned = currentAssignments;
  const max = maxAssignments;
  const progress = Math.floor((assigned / max) * 100);
  const [assigningUserId, setAssigningUserId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [openCreateCrew, setOpenCreateCrew] = useState(false);

  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [rolesCount, setRolesCount] = useState(0);

  const [callSheetAssignments, setCallSheetAssignments] = useState<
    CallSheetAssignment[]
  >([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const { id: streamId } = useParams();

  const fetchCallSheetAssignments = useCallback(async () => {
    if (!streamId) return;

    try {
      setAssignmentsLoading(true);
      const response = await apiService.get<{ results: CallSheetAssignment[] }>(
        `/api/call-sheets/callsheet/?stream=${streamId}`
      );
      setCallSheetAssignments(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch callsheet assignments:", error);
      toast.error("Failed to load existing assignments");
      setCallSheetAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    if (open && streamId) {
      fetchCallSheetAssignments();
    }
  }, [fetchCallSheetAssignments, open, streamId]);

  const isUserAssignedToRole = (
    userId: number,
    roleId: number | null
  ): boolean => {
    if (!roleId) return false;

    return callSheetAssignments.some(
      (assignment) =>
        assignment.crew_member.id === userId &&
        assignment.roles.includes(roleId)
    );
  };

  const fetchCrewMembers = useCallback(async () => {
    setLoading(true);

    try {
      let url = "/api/crews/crew-members/";

      if (searchTerm.trim().length >= 3) {
        url += `?search=${encodeURIComponent(searchTerm.trim())}`;
      } else if (selectedRole) {
        url += `?roles=${selectedRole}&page_size=20`;
      } else {
        setCrewMembers([]);
        return;
      }

      const response = await apiService.get<{
        results: IUser[];
        count: number;
      }>(url);

      setCrewMembers(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch {
      toast.error("Failed to load crew members");
      setCrewMembers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedRole]);

  useEffect(() => {
    if (selectedRole) {
      fetchCrewMembers();
    } else {
      setCrewMembers([]);
    }
  }, [fetchCrewMembers, selectedRole, page]);

  useEffect(() => {
    if (!selectedRole) return;

    if (searchTerm.trim().length === 0) {
      setPage(1);
      fetchCrewMembers();
      return;
    }

    if (searchTerm.trim().length < 3) return;

    const debounce = setTimeout(() => {
      setPage(1);
      fetchCrewMembers();
    }, 400);

    return () => clearTimeout(debounce);
  }, [fetchCrewMembers, searchTerm, selectedRole]);

  const handleAssign = async (user: IUser) => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    try {
      setAssigningUserId(user.id);
      const wasAssigned = await onAssign(user, [selectedRole]);

      if (!wasAssigned) {
        return;
      }

      setCrewMembers((prev) =>
        prev.map((member) =>
          member.id === user.id
            ? { ...member, roles: [...(member.roles || []), selectedRole] }
            : member
        )
      );

      await fetchCallSheetAssignments();

      toast.success(
        `${user.user.first_name} ${user.user.last_name} assigned successfully!`
      );
    } catch {
      // error already handled upstream
    } finally {
      setAssigningUserId(null);
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedRole(null);
    setCrewMembers([]);
    setPage(1);
    onSuccessClose();
    onClose();
  };

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  const fetchRoles = async (pagination: GridPaginationModel) => {
    try {
      setRolesLoading(true);
      setRolesError(null);

      const res = await apiService.get<{
        count: number;
        results: Role[];
      }>(
        `/api/crews/roles/?page=${pagination.page + 1}&page_size=${
          pagination.pageSize
        }`
      );

      setRoleOptions(res.data.results);
      setRolesCount(res.data.count);
    } catch {
      setRolesError("Failed to load roles");
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchRoles(pagination);
  }, [open, pagination]);

  useEffect(() => {
    if (!open) {
      setSelectedRole(null);
      setPagination({
        page: 0,
        pageSize: 20,
      });
    }
  }, [open]);

  const handleCreateUser = async (
    userData: Omit<IUser, "id">
  ): Promise<IUser> => {
    try {
      const response = await apiService.post<IUser>(
        "/api/crews/crew-members/full-create/",
        userData
      );
      const newUser = response.data;
      toast.success(`User '${newUser.user.first_name}' created successfully!`);
      return newUser;
    } catch (err: unknown) {
      let errorMessage = "Failed to create user. Please try again.";
      const errorResponse = (err as {
        response?: { status?: number; data?: { detail?: string } };
      })?.response;
      if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        toast.error(errorMessage);
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
        toast.error(`Error: ${errorMessage}`);
      } else if (errorResponse?.data) {
        const serverErrors = Object.values(errorResponse.data).flat().join(" ");
        errorMessage = serverErrors || errorMessage;
        toast.error(`Error: ${errorMessage}`);
      }
      throw new Error(errorMessage);
    }
  };

  const roleOrder = [
    "EXECUTIVE PRODUCER",
    "Producer",
    "Assistant Producer",
    "Liveswitcher",
    "Camera",
    "Networking",
    "Sound",
    "Tribute Table",
  ];
  const sortedRoles = [...roleOptions].sort((a, b) => {
    const indexA = roleOrder.findIndex(
      (roleName) => roleName.toUpperCase() === a.name.toUpperCase()
    );
    const indexB = roleOrder.findIndex(
      (roleName) => roleName.toUpperCase() === b.name.toUpperCase()
    );

    const safeIndexA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
    const safeIndexB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

    return safeIndexA - safeIndexB;
  });
  useEffect(() => {
    if (!selectedRole && sortedRoles.length > 0) {
      setSelectedRole(sortedRoles[0].id);
    }
  }, [sortedRoles, selectedRole]);

  return (
    <Box>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: "95vh" } }}
      >
        <DialogTitle
          sx={{ p: 2, display: "flex", justifyContent: "space-between" }}
        >
          <Typography variant="h6" fontWeight="bold">
            Assign Crew
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", gap: 2, height: "70vh" }}>
          <Grid container sx={{ height: "70vh", flexWrap: "nowrap" }}>
            {/* LEFT PANEL */}
            <Grid
              size={{ xs: 12, md: 5 }}
              sx={{
                p: 3,
                borderRight: `1px solid ${colors.primary[700]}`,
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
                maxHeight: "70vh",
                overflowY: "auto",
                minWidth: "320px",
                maxWidth: "320px",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  backgroundColor: reduxTheme.primary_colors[800],
                  p: 2,
                  borderRadius: 2,
                }}
              >
                {/* Assigned count */}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption">Assigned:</Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ mb: 2 }}
                />

                {/* Loading */}
                {rolesLoading && (
                  <Chip
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <i>Fetching roles...</i>
                      </Box>
                    }
                    size="small"
                    variant="outlined"
                  />
                )}

                {/* Error */}
                {rolesError && (
                  <Alert severity="error">
                    {rolesError}
                    <Button onClick={() => fetchRoles(pagination)}>
                      Retry
                    </Button>
                  </Alert>
                )}

                {/* Roles */}
                {!rolesLoading && !rolesError && (
                  <>
                    <Typography variant="caption">Roles</Typography>

                    {sortedRoles.map((role) => {
                      return (
                        <Box
                          key={role.id}
                          onClick={() => setSelectedRole(role.id)}
                          sx={{
                            p: 1,
                            mb: 1,
                            borderRadius: 2,
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor:
                              selectedRole === role.id
                                ? reduxTheme.primary_colors[400]
                                : reduxTheme.primary_colors[900],
                            backgroundColor: reduxTheme.primary_colors[900],
                          }}
                          display="flex"
                          justifyContent="space-between"
                        >
                          <Box display="flex" gap={1} alignItems="center">
                            <Box sx={{ width: 16, height: 16 }} />

                            <Typography variant="caption">
                              {role.name}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}

                    {/* Pagination */}
                    <Paginator
                      count={rolesCount}
                      pagination={pagination}
                      setPagination={setPagination}
                    />
                  </>
                )}
              </Box>
            </Grid>

            {/* RIGHT PANEL */}
            <Grid
              size={{ xs: 12, md: 7 }}
              sx={{
                p: 3,
                maxHeight: "70vh",
                overflowY: "auto",
                flex: 1,
                minWidth: 0,
              }}
            >
              <CustomTextField
                fullWidth
                placeholder="Search crew member..."
                helperText="Type at least 3 characters to search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                }}
              />

              {assignmentsLoading && (
                <Box textAlign="center" mb={2}>
                  <CircularProgress size={20} />
                  <Typography variant="caption" display="block" mt={1}>
                    Loading assignments...
                  </Typography>
                </Box>
              )}

              {!selectedRole ? (
                <Typography textAlign="center" color="text.secondary">
                  Select a role to view crew members
                </Typography>
              ) : loading ? (
                <Box textAlign="center">
                  <CircularProgress />
                </Box>
              ) : crewMembers.length === 0 ? (
                <Box textAlign="center">
                  <Typography color="text.secondary" mb={2}>
                    No crew members found
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={() => setOpenCreateCrew(true)}
                  >
                    Add New Crew Member
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {crewMembers.map((crw) => {
                    const isAlreadyAssigned = isUserAssignedToRole(
                      crw.id,
                      selectedRole
                    );

                    return (
                      <Grid
                        size={{ xs: 12, sm: 6, md: 4 }}
                        key={crw.id}
                        sx={{
                          backgroundColor: colors.primary[800],
                          p: 2,
                          borderRadius: 2,
                        }}
                      >
                        {/* Top section */}
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={1}
                        >
                          <Avatar
                            src={
                              crw?.user?.profile_picture
                                ? crw?.user?.profile_picture
                                : "/placeholder.svg"
                            }
                            sx={{ width: 64, height: 64 }}
                          />
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <Typography variant="subtitle2" textAlign="center">
                              {crw?.user?.first_name} {crw?.user?.last_name}
                            </Typography>

                            <Rating
                              size="small"
                              value={crw?.average_rating || 0}
                              precision={0.1}
                              readOnly
                            />
                          </Box>
                        </Box>

                        {/* Bottom section */}
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Chip
                            label="Available"
                            size="small"
                            sx={{ my: 1, borderRadius: 20 }}
                          />

                          <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                            {/* Phone */}
                            <Tooltip
                              title={
                                crw?.user?.individual?.phone_number ||
                                "No phone number"
                              }
                            >
                              <IconButton
                                disabled={!crw?.user?.individual?.phone_number}
                                onClick={() =>
                                  crw?.user?.individual.phone_number &&
                                  window.open(
                                    `tel:${crw?.user?.individual?.phone_number}`
                                  )
                                }
                                sx={{
                                  color: crw?.user?.individual?.phone_number
                                    ? colors.primary[400]
                                    : colors.primary[800],
                                  opacity: crw?.user?.individual?.phone_number
                                    ? 1
                                    : 0.5,
                                }}
                              >
                                <Call fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* Email */}
                            <Tooltip
                              title={crw.user.email || "No email address"}
                            >
                              <IconButton
                                disabled={!crw.user.email}
                                onClick={() =>
                                  crw.user.email &&
                                  window.open(`mailto:${crw.user.email}`)
                                }
                                sx={{
                                  color: crw.user.email
                                    ? colors.primary[400]
                                    : colors.primary[800],
                                  opacity: crw.user.email ? 1 : 0.5,
                                }}
                              >
                                <Email fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Tooltip
                            title={
                              isAlreadyAssigned
                                ? "Already assigned to this role"
                                : ""
                            }
                          >
                            <span style={{ width: "100%" }}>
                              <Button
                                fullWidth
                                variant="contained"
                                disabled={
                                  assigningUserId === crw.id ||
                                  isAlreadyAssigned
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssign(crw);
                                }}
                              >
                                {assigningUserId === crw.id ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : isAlreadyAssigned ? (
                                  "Assigned"
                                ) : (
                                  "Assign"
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Grid>
                    );
                  })}
                  {crewMembers.length < totalCount && (
                    <Box textAlign="center" mt={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setPage((prev) => prev + 1);
                          fetchCrewMembers();
                        }}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} /> : "Load More"}
                      </Button>
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>CLOSE</Button>
        </DialogActions>
      </Dialog>
      <AddCrewModal
        open={openCreateCrew}
        onClose={() => setOpenCreateCrew(false)}
        onAddCrew={async (user, _roles) => {
          void _roles;
          setOpenCreateCrew(false);
          setCrewMembers((prev) => [user, ...prev]);
          return Promise.resolve();
        }}
        onCreateUser={handleCreateUser}
        fetchCrewMembers={fetchCrewMembers}
        availableRoles={availableRoles}
        theme={theme}
        colors={colors}
        mode="create-only"
      />
    </Box>
  );
};

export default AssignCrewModal;
